import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Transforme une erreur Supabase / Postgres en message FR utilisable.
 * Codes Postgres courants : 23505 (unique), 23503 (FK), 23502 (not null), 42501 (RLS).
 */
export function parseSupabaseError(err: any, fallback = "Une erreur est survenue"): string {
  if (!err) return fallback;
  const code: string | undefined = err?.code;
  const msg: string = err?.message || "";

  if (code === "42501" || /row-level security|violates row-level/i.test(msg)) {
    return "Permissions insuffisantes pour effectuer cette action.";
  }
  if (code === "23505" || /duplicate key/i.test(msg)) {
    return "Cet enregistrement existe déjà.";
  }
  if (code === "23503" || /foreign key/i.test(msg)) {
    return "Référence invalide : l'élément lié n'existe pas ou plus.";
  }
  if (code === "23502" || /not-null/i.test(msg)) {
    return "Un champ obligatoire est manquant.";
  }
  if (code === "22P02" || /invalid input syntax/i.test(msg)) {
    return "Format de données invalide.";
  }
  if (/network|fetch failed|timeout/i.test(msg)) {
    return "Problème de connexion réseau. Réessayez.";
  }
  return msg || fallback;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function isUuid(v: unknown): v is string {
  return typeof v === "string" && UUID_RE.test(v);
}

/**
 * Met une unité au pluriel quand la quantité dépasse 1.
 * Gère les unités françaises courantes et les cas en -al / -au / -eau.
 * Ne touche pas aux unités déjà invariables (ex: "kg", "L", "cl", "g", "ml").
 */
const INVARIABLE_UNITS = new Set([
  "kg", "g", "mg", "t",
  "l", "cl", "ml", "dl",
  "m", "cm", "mm",
  "u", "pcs", "pc",
  "%",
]);

export function pluralize(qty: number, unit: string | null | undefined): string {
  if (!unit) return "";
  const trimmed = unit.trim();
  if (!trimmed) return "";
  if (Math.abs(qty) <= 1) return trimmed;
  const lower = trimmed.toLowerCase();
  if (INVARIABLE_UNITS.has(lower)) return trimmed;
  // Déjà au pluriel ?
  if (/s$/i.test(trimmed) || /x$/i.test(trimmed)) return trimmed;
  // -al → -aux (sauf bal, carnaval, festival, récital, régal — rares ici)
  if (/al$/i.test(trimmed)) return trimmed.replace(/al$/i, "aux");
  // -au, -eau, -eu → +x
  if (/(au|eu)$/i.test(trimmed)) return trimmed + "x";
  return trimmed + "s";
}

